import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { useNavigate } from 'react-router-dom';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import ServiceCardManager from '../../components/admin/ServiceCardManager';
import {
    Building2, Home, Type, PanelBottom, Mail, HardHat, DollarSign, Zap, Mountain,
    LogOut, Save, Image as ImageIcon, CheckCircle2, AlertCircle, Loader2, Layout,
    FileText, RefreshCw, MapPin, Target, Phone, Info, Briefcase, Plane, Truck,
    Star, Wrench, Users, ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminAlYasminah: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'content' | 'images'>('content');
    const [activeSection, setActiveSection] = useState<'banner' | 'overview' | 'services' | 'why_us' | 'contact'>('banner');
    const [content, setContent] = useState<any>({});
    const [saving, setSaving] = useState<string | null>(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [pendingImages, setPendingImages] = useState<Record<string, { file: File, preview: string }>>({});
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) { navigate('/admin/login'); return; }
        fetchContent();
    }, [navigate]);

    const fetchContent = async () => {
        try {
            const res = await fetch('/api/homepage/content');
            const data = await res.json();
            setContent(data);
        } catch (err) { console.error('Fetch error', err); }
    };

    const updateField = (key: string, value: string) => {
        setContent((prev: any) => ({
            ...prev,
            AlYasminah: { ...(prev.AlYasminah || {}), [key]: value }
        }));
    };

    const saveContent = async () => {
        setSaving('AlYasminah');
        setMessage({ text: 'Saving...', type: '' });
        try {
            const sectionData = content.AlYasminah || {};
            // Filter keys to exclude _ar keys as they are sent alongside the main key
            const mainKeys = Object.keys(sectionData).filter(key => !key.endsWith('_ar'));
            
            const updates = mainKeys.map(key => {
                const value = sectionData[key];
                const valueAr = sectionData[`${key}_ar`] || null;
                
                return fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
                    body: JSON.stringify({ 
                        section: 'AlYasminah', 
                        content_key: key, 
                        content_value: value,
                        content_value_ar: valueAr
                    })
                });
            });
            await Promise.all(updates);
            setMessage({ text: 'Content saved successfully!', type: 'success' });
            fetchContent();
        } catch {
            setMessage({ text: 'Save failed', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 4000);
        }
    };

    const handleImageSelect = (key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({ ...prev, [key]: { file, preview } }));
    };

    const saveImages = async () => {
        setSaving('images');
        setMessage({ text: 'Uploading images...', type: '' });
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const formData = new FormData();
                formData.append('section', 'AlYasminah');
                formData.append('content_key', key);
                formData.append('image', pending.file);
                return fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
                    body: formData
                });
            });
            const results = await Promise.all(promises);
            if (results.every(r => r.ok)) {
                setMessage({ text: 'Images uploaded!', type: 'success' });
                setPendingImages({});
                fetchContent();
            } else {
                setMessage({ text: 'Some uploads failed.', type: 'error' });
            }
        } catch {
            setMessage({ text: 'Upload error', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 4000);
        }
    };

    const T = content.AlYasminah || {};

    const inputCls = "w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-slate-800";
    const textCls = "w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium leading-relaxed text-slate-700";
    const labelCls = "text-[10px] font-bold text-slate-400 uppercase tracking-widest";

    const UploadBox = ({ keyName, label, currentImage }: any) => {
        const pending = pendingImages[keyName];
        const displayImage = pending ? pending.preview : currentImage;
        return (
            <div className="group bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1 text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-lg text-[8px] font-bold uppercase">
                            <CheckCircle2 className="w-3 h-3" /> Live
                        </div>
                    )}
                </div>
                <div className="relative aspect-video bg-slate-50 rounded-xl overflow-hidden mb-4 border border-slate-100">
                    {displayImage ? (
                        <img src={displayImage} className="w-full h-full object-cover" alt="Preview" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <ImageIcon className="w-8 h-8 opacity-20" />
                        </div>
                    )}
                    <label className="absolute inset-0 cursor-pointer bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-white" />
                        <input type="file" className="hidden" accept="image/*" onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) handleImageSelect(keyName, file);
                        }} />
                    </label>
                </div>
                {pending && <div className="text-[8px] text-amber-500 font-bold uppercase text-center">Unsaved Changes</div>}
            </div>
        );
    };

    const sections = [
        { id: 'banner', label: 'Hero Banner', icon: Layout },
        { id: 'overview', label: 'Overview', icon: Building2 },
        { id: 'services', label: 'Services', icon: Wrench },
        { id: 'why_us', label: 'Why Choose Us', icon: Star },
        { id: 'contact', label: 'Contact', icon: Phone },
    ];



    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />


            {/* Main */}
            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Building2 className="w-6 h-6 text-blue-600" /> Al Yasminah Real Estate Editor
                        </h2>
                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button onClick={() => setActiveTab('content')} className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'content' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>
                                <Type className="w-4 h-4" /><span>Content</span>
                            </button>
                            <button onClick={() => setActiveTab('images')} className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'images' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>
                                <ImageIcon className="w-4 h-4" /><span>Media</span>
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200">
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10">
                    <div className="max-w-6xl mx-auto">
                        <AnimatePresence mode="wait">
                            {activeTab === 'content' ? (
                                <motion.div key="content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-8">
                                    {/* Section nav */}
                                    <div className="flex flex-wrap gap-3 mb-8">
                                        {sections.map(sec => (
                                            <button key={sec.id} onClick={() => setActiveSection(sec.id as any)}
                                                className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all border ${activeSection === sec.id ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                                                <sec.icon className="w-4 h-4" /><span>{sec.label}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}

                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                                        <div className="flex items-center justify-between mb-8">
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center capitalize">
                                                <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                                                {activeSection} Configuration
                                            </h3>
                                            <button onClick={saveContent} disabled={!!saving}
                                                className="flex items-center space-x-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-xl font-bold text-sm transition-all shadow-lg active:scale-95">
                                                {saving === 'AlYasminah' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                <span>Save Changes</span>
                                            </button>
                                        </div>

                                        {/* BANNER */}
                                        {activeSection === 'banner' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="Banner Title"
                                                    englishValue={T.banner_title ?? 'Al Yasminah Real Estate'}
                                                    arabicValue={T.banner_title_ar}
                                                    onChangeEnglish={val => updateField('banner_title', val)}
                                                    onChangeArabic={val => updateField('banner_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="Banner Subtitle"
                                                    englishValue={T.banner_subtitle ?? 'Professionally managed residential building with a focus on cleanliness, safety, and tenant comfort.'}
                                                    arabicValue={T.banner_subtitle_ar}
                                                    onChangeEnglish={val => updateField('banner_subtitle', val)}
                                                    onChangeArabic={val => updateField('banner_subtitle_ar', val)}
                                                    rows={2}
                                                />
                                            </div>
                                        )}

                                        {/* OVERVIEW */}
                                        {activeSection === 'overview' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="About Title"
                                                    englishValue={T.about_title ?? 'About Al Yasminah Real Estate'}
                                                    arabicValue={T.about_title_ar}
                                                    onChangeEnglish={val => updateField('about_title', val)}
                                                    onChangeArabic={val => updateField('about_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="About Description"
                                                    englishValue={T.about_desc ?? ''}
                                                    arabicValue={T.about_desc_ar}
                                                    onChangeEnglish={val => updateField('about_desc', val)}
                                                    onChangeArabic={val => updateField('about_desc_ar', val)}
                                                    rows={6}
                                                />
                                            </div>
                                        )}

                                        {/* SERVICES */}
                                        {activeSection === 'services' && (
                                            <ServiceCardManager
                                                section="AlYasminah"
                                                data={T}
                                                onSaved={fetchContent}
                                                sectionTitle={T.services_title ?? 'Building Management & Maintenance'}
                                                sectionTitleAr={T.services_title_ar}
                                                onChangeSectionTitle={val => updateField('services_title', val)}
                                                onChangeSectionTitleAr={val => updateField('services_title_ar', val)}
                                            />
                                        )}

                                        {/* WHY CHOOSE US */}
                                        {activeSection === 'why_us' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="Section Title"
                                                    englishValue={T.why_title ?? 'Why Choose Us?'}
                                                    arabicValue={T.why_title_ar}
                                                    onChangeEnglish={val => updateField('why_title', val)}
                                                    onChangeArabic={val => updateField('why_title_ar', val)}
                                                />
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    {[1, 2, 3, 4, 5, 6].map(i => (
                                                        <BilingualInput 
                                                            key={i}
                                                            label={`Point ${i}`}
                                                            englishValue={T[`why_${i}`] ?? ''}
                                                            arabicValue={T[`why_${i}_ar`]}
                                                            onChangeEnglish={val => updateField(`why_${i}`, val)}
                                                            onChangeArabic={val => updateField(`why_${i}_ar`, val)}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* CONTACT */}
                                        {activeSection === 'contact' && (
                                            <div className="space-y-6">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                    {[
                                                        { key: 'contact_title', label: 'Section Title', def: 'Contact Us' },
                                                        { key: 'contact_subtitle', label: 'Subtitle', def: 'Get in touch for leasing inquiries.' },
                                                        { key: 'contact_address', label: 'Address', def: 'Al Nahda, Sharjah, UAE' },
                                                        { key: 'contact_phone', label: 'Phone', def: '+971 6 524 8861' },
                                                        { key: 'contact_email', label: 'Email', def: 'Alyasminha.realestate@hamdanoman.com' },
                                                        { key: 'contact_hours', label: 'Office Hours', def: 'Sunday to Thursday, 10:00 AM – 6:00 PM' },
                                                        { key: 'contact_map_url', label: 'Google Maps URL', def: 'https://maps.app.goo.gl/...' },
                                                        { key: 'contact_map_embed', label: 'Maps Embed URL (iframe src)', def: '' },
                                                    ].map(f => (
                                                        <BilingualInput 
                                                            key={f.key}
                                                            label={f.label}
                                                            englishValue={T[f.key] ?? f.def}
                                                            arabicValue={T[`${f.key}_ar`]}
                                                            onChangeEnglish={val => updateField(f.key, val)}
                                                            onChangeArabic={val => updateField(`${f.key}_ar`, val)}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div key="images" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="space-y-8">
                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex-1 mr-6">
                                            <h3 className="text-xl font-black text-slate-800">Media Management — Al Yasminah</h3>
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Hero banners and section images</p>
                                        </div>
                                        <button onClick={saveImages} disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                            className="px-10 py-5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-3xl font-black text-xs uppercase tracking-widest transition-all shadow-2xl flex items-center space-x-3">
                                            {saving === 'images' ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
                                            <span>Upload {Object.keys(pendingImages).length} Asset(s)</span>
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        <UploadBox keyName="banner_1" label="Hero Banner 1" currentImage={T.banner_1} />
                                        <UploadBox keyName="banner_2" label="Hero Banner 2" currentImage={T.banner_2} />
                                        <UploadBox keyName="banner_3" label="Hero Banner 3" currentImage={T.banner_3} />
                                        <UploadBox keyName="about_image" label="Building Photo" currentImage={T.about_image} />
                                        <UploadBox keyName="svc_1_image" label="Service 1 Image" currentImage={T.svc_1_image} />
                                        <UploadBox keyName="svc_2_image" label="Service 2 Image" currentImage={T.svc_2_image} />
                                        <UploadBox keyName="svc_3_image" label="Service 3 Image" currentImage={T.svc_3_image} />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminAlYasminah;
